<template>
    <b-dropdown
        size="sm"
        :variant="isFilterActive ? 'primary' : 'outline-secondary'"
        no-caret
        boundary="window"
        class="grid-filter-chip filter-multiselect-chip"
        :disabled="disabled"
    >
        <template #button-content>
            <i :class="chipIcon"></i>
            <span class="font-weight-bold mr-1">{{ label }}:</span>
            <span>{{ displayText }}</span>
            <i class="bi bi-chevron-down ml-1 filter-chevron"></i>
        </template>

        <b-dropdown-form class="filter-multiselect-dropdown">
            <div class="d-flex justify-content-between align-items-center mb-1 pb-1 border-bottom filter-multiselect-header">
                <small class="font-weight-bold text-muted mr-3">Filter by {{ label }}</small>
                <div>
                    <b-button size="sm" variant="link" class="p-0 mr-2 text-decoration-none filter-action-btn" @click="selectAll">{{ allText }}</b-button>
                    <b-button v-if="allowEmpty" size="sm" variant="link" class="p-0 text-decoration-none text-danger filter-action-btn" @click="clearAll">{{ clearText }}</b-button>
                </div>
            </div>

            <div class="filter-options-list">
                <div
                    v-for="opt in normalizedOptions"
                    :key="opt.value"
                    class="filter-option-item"
                >
                    <b-form-checkbox
                        :checked="isSelected(opt.value)"
                        :disabled="!allowEmpty && isSelected(opt.value) && selectedCount <= 1"
                        :title="!allowEmpty && isSelected(opt.value) && selectedCount <= 1 ? `At least one ${label.toLowerCase()} must be selected` : ''"
                        class="m-0 compact-checkbox"
                        @change="onCheckboxChange(opt.value, $event)"
                    >
                        <span class="filter-option-text">{{ opt.text }}</span>
                    </b-form-checkbox>
                </div>
            </div>
        </b-dropdown-form>
    </b-dropdown>
</template>

<script>
export default {
    name: 'FilterMultiSelect',
    props: {
        value: {
            type: Array,
            default: () => []
        },
        label: {
            type: String,
            default: 'Filter'
        },
        icon: {
            type: String,
            default: 'bi bi-funnel'
        },
        options: {
            type: Array,
            default: () => []
        },
        allLabel: {
            type: String,
            default: 'All'
        },
        noneLabel: {
            type: String,
            default: 'None'
        },
        allText: {
            type: String,
            default: 'All'
        },
        clearText: {
            type: String,
            default: 'Clear'
        },
        countFormat: {
            type: String,
            default: ''
        },
        activeCondition: {
            type: String,
            default: 'not-all'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        allowEmpty: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        normalizedOptions() {
            return (this.options || []).map(opt => {
                if (typeof opt === 'string') {
                    return { value: opt, text: opt };
                }
                return opt;
            });
        },
        selectedCount() {
            return (this.value || []).length;
        },
        totalCount() {
            return this.normalizedOptions.length;
        },
        isFilterActive() {
            if (this.activeCondition === 'any') {
                return this.selectedCount > 0;
            }
            return this.selectedCount !== this.totalCount;
        },
        chipIcon() {
            if (this.activeCondition === 'any') {
                return this.selectedCount > 0
                    ? `${this.icon}-fill mr-1`
                    : `${this.icon} mr-1`;
            }
            return this.isFilterActive
                ? `${this.icon}-fill mr-1`
                : `${this.icon} mr-1`;
        },
        displayText() {
            if (this.selectedCount === 0) {
                return this.noneLabel;
            }
            if (this.selectedCount === this.totalCount) {
                return this.allLabel;
            }
            if (this.countFormat) {
                return this.countFormat.replace('{count}', this.selectedCount);
            }
            return this.normalizedOptions
                .filter(o => (this.value || []).includes(o.value))
                .map(o => o.text)
                .join(', ');
        }
    },
    methods: {
        isSelected(val) {
            return (this.value || []).includes(val);
        },
        emitChange(newVal) {
            this.$emit('input', newVal);
            this.$emit('change', newVal);
        },
        selectAll() {
            const allVals = this.normalizedOptions.map(o => o.value);
            this.emitChange(allVals);
        },
        clearAll() {
            if (!this.allowEmpty) return;
            this.emitChange([]);
        },
        onCheckboxChange(val, isChecked) {
            let next = [...(this.value || [])];
            if (isChecked && !next.includes(val)) {
                next.push(val);
            } else if (!isChecked && next.includes(val)) {
                if (!this.allowEmpty && next.length <= 1) {
                    return;
                }
                next = next.filter(v => v !== val);
            }
            this.emitChange(next);
        }
    }
};
</script>

<style scoped>
.filter-multiselect-dropdown {
    padding: 0.35rem 0.5rem !important;
    min-width: 210px;
    width: max-content;
    max-width: 360px;
}
.filter-multiselect-header {
    font-size: 0.75rem;
}
.filter-options-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.filter-option-item {
    display: flex;
    align-items: center;
    border-radius: 4px;
    padding: 0.2rem 0.4rem;
    transition: background-color 0.12s ease;
}
.filter-option-item:hover {
    background-color: rgba(0, 0, 0, 0.05);
}
[data-theme="dark"] .filter-option-item:hover {
    background-color: rgba(255, 255, 255, 0.08);
}
.compact-checkbox {
    font-size: 0.82rem;
    line-height: 1.3;
    margin-bottom: 0 !important;
    width: 100%;
}
.compact-checkbox >>> .custom-control-label {
    cursor: pointer;
    font-size: 0.82rem;
    padding-top: 1px;
    user-select: none;
    white-space: nowrap;
    width: 100%;
}
.compact-checkbox >>> .custom-control-input:focus ~ .custom-control-label::before {
    box-shadow: none;
}
.filter-option-text {
    font-weight: 500;
    white-space: nowrap;
}
.filter-action-btn {
    font-size: 0.75rem;
}
</style>
